import { clone } from "ramda";

import type {
  InputActivityInstanceInput,
  InputConnector,
  InputWorkflowEventInput,
  InputWorkflowInput,
} from "@/graphql.ts";
import type { Activity } from "@/modules/workflow/utils/types.ts";

/**
 *
 * @param activities
 * @param connections
 * @returns True if any connection has no corresponding source and target activity
 */
export function hasInvalidConnections(activities: InputActivityInstanceInput[], connections: InputConnector[]) {
  return connections.some((connection) => isInvalidConnection(activities, connection));
}

/**
 * Fixes the connections list by removing connections that have no corresponding source and target activity.
 *
 * @param saveData
 * @returns An updated connections list containing no invalid activities, or the provided list if there are no invalid connections
 */
export function validateAndFixConnections<T extends InputWorkflowInput | InputWorkflowEventInput>(saveData: T): T {
  // validate connections
  const isInvalid = hasInvalidConnections(saveData.config.activities, saveData.config.connectors);

  if (isInvalid) {
    let output = clone(saveData);

    const cleanedConnections = output.config.connectors.filter(
      (connection) => !isInvalidConnection(output.config.activities, connection),
    );

    // update payload and store if connections are invalid
    // update payload to save
    output = {
      ...output,
      config: { ...output.config, connectors: cleanedConnections },
    };

    return output;
  }

  return saveData;
}

/**
 *
 * @param activities
 * @param connection
 * @returns True if the provided connection has no corresponding source and target activity
 */
export function isInvalidConnection(
  activities: Pick<InputActivityInstanceInput, "identifier">[],
  connection: Pick<InputConnector, "source" | "target">,
) {
  const ids = new Set(activities.map((a) => a.identifier));
  return !ids.has(connection.source) || !ids.has(connection.target);
}

/**
 * Narrows activity instances to those whose definition still exists.
 */
export function filterValidActivityInstances<T extends { identifier: string; name: string; version: string }>(
  instances: T[],
  definitions: Activity[],
): T[] {
  return instances.filter((instance) =>
    definitions.some((def) => def.name === instance.name && def.version.name === instance.version),
  );
}
